/**
 * AdminUsersPage — /coach/admin/users
 *
 * Unified user management for administrators, owners, and platform_admins.
 * Features:
 *   - Paginated, filterable list of all learner accounts
 *   - Create user of any role (+ Bulk Import)
 *   - Per-user detail drawer: edit profile, reset password, assign track,
 *     deactivate, or permanently delete
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { adminUserAPI, adminOrgAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import useOrgScope from '../../hooks/useOrgScope';
import CoachLayout from './CoachLayout';

const AI_TRACK_OPTIONS = [
  { track_id: 'ai_fundamentals', display_name: 'AI Fundamentals' },
  { track_id: 'ai_orchestrator_certificate', display_name: 'AI Orchestrator Certificate' },
  { track_id: 'ai_entrepreneur_certificate', display_name: 'AI Entrepreneur Certificate' },
];

// ── Color scheme ───────────────────────────────────────────────────────────────
const C = {
  bg: '#1E1E24',
  surface: '#2a2a32',
  border: '#3a3a44',
  accent: '#92140C',
  accentLight: '#b91c1c',
  cream: '#FFF8F0',
  peach: '#FFCF99',
  text: '#FFF8F0',
  muted: '#9ca3af',
  green: '#22c55e',
  red: '#ef4444',
  yellow: '#f59e0b',
};

const ROLE_COLORS = {
  platform_admin: '#7c3aed',
  owner: '#c2410c',
  administrator: '#1d4ed8',
  coach: '#0d9488',
  coach_learner: '#0d9488',
  learner: '#374151',
  admin: '#1d4ed8',
};

const ROLE_OPTIONS = [
  'learner', 'coach_learner', 'coach', 'administrator', 'owner', 'platform_admin',
];

const ROLE_LABELS = {
  learner: 'Learner — enrolled in tracks only',
  coach_learner: 'Coach + Learner — can author and take courses',
  coach: 'Coach — authors content, no learner enrollment',
  administrator: 'Administrator — manages users/orgs for their domains',
  owner: 'Owner — billing + administrator rights',
  platform_admin: 'Platform Admin — Flowgenix staff full access',
};

const DOMAIN_ROLE_OPTIONS = [
  { value: 'domain_admin', label: 'Domain Admin — full control' },
  { value: 'domain_editor', label: 'Domain Editor — can edit content' },
  { value: 'domain_viewer', label: 'Domain Viewer — read-only' },
];

// ── Helpers ────────────────────────────────────────────────────────────────────

function RolePill({ role }) {
  const color = ROLE_COLORS[role] || '#374151';
  return (
    <span style={{
      background: color, color: '#fff', borderRadius: 4,
      padding: '2px 8px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
    }}>
      {role}
    </span>
  );
}

function StatusDot({ isActive }) {
  return (
    <span style={{
      display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
      background: isActive ? C.green : C.red, marginRight: 6,
    }} />
  );
}

// ── User Detail Drawer ─────────────────────────────────────────────────────────

export function UserDrawer({ user, onClose, onUpdated, onRefreshList }) {
  const { assignableDomains: scopeDomains, orgIds, unrestricted } = useOrgScope();
  const [tab, setTab] = useState('profile');
  const [profileForm, setProfileForm] = useState({
    display_name: user.display_name || '',
    email: user.email || '',
    preferred_language: user.preferred_language || 'en',
    role: user.role || 'learner',
  });
  const [enrolled, setEnrolled] = useState(user.enrolled_domains || []);
  const [domainSel, setDomainSel] = useState(() => new Set(user.enrolled_domains || []));
  const [trackForm, setTrackForm] = useState({ domain_id: '', track_id: '', org_id: '' });
  const [tempPassword, setTempPassword] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [orgOptions, setOrgOptions] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState('');

  useEffect(() => {
    adminOrgAPI.listOrgs()
      .then(({ data }) => {
        const list = Array.isArray(data?.orgs) ? data.orgs : (Array.isArray(data) ? data : []);
        const allowed = unrestricted || !orgIds.length
          ? list
          : list.filter((o) => orgIds.includes(o.org_id));
        setOrgOptions(allowed);
      })
      .catch(() => setOrgOptions([]));
  }, [orgIds, unrestricted]);

  const showMsg = (text, isError = false) => {
    setMsg({ text, isError });
    setTimeout(() => setMsg(null), 4000);
  };

  const saveDomains = async () => {
    const offered = scopeDomains.map((d) => d.domain_id);
    const current = new Set(enrolled);
    const add = offered.filter((d) => domainSel.has(d) && !current.has(d));
    const remove = offered.filter((d) => !domainSel.has(d) && current.has(d));
    if (!add.length && !remove.length) { showMsg('No domain changes.'); return; }
    setSaving(true);
    try {
      const { data } = await adminUserAPI.updateDomains(user.learner_id, { add, remove });
      const next = data?.enrolled_domains || [];
      setEnrolled(next);
      setDomainSel(new Set(next));
      showMsg('Domains updated.');
      onUpdated();
    } catch (e) {
      showMsg(e?.response?.data?.detail || 'Domain update failed.', true);
    } finally {
      setSaving(false);
    }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (profileForm.display_name !== user.display_name) payload.display_name = profileForm.display_name;
      if (profileForm.email !== user.email) payload.email = profileForm.email;
      if (profileForm.preferred_language !== user.preferred_language) payload.preferred_language = profileForm.preferred_language;
      if (profileForm.role !== user.role) payload.role = profileForm.role;
      if (!Object.keys(payload).length) { showMsg('No changes to save.'); return; }
      await adminUserAPI.updateProfile(user.learner_id, payload);
      showMsg('Profile updated.');
      onUpdated();
    } catch (e) {
      showMsg(e?.response?.data?.detail || 'Failed to save.', true);
    } finally {
      setSaving(false);
    }
  };

  const resetPassword = async () => {
    if (!window.confirm(`Reset password for ${user.email}? A temporary password will be generated.`)) return;
    setSaving(true);
    setTempPassword(null);
    try {
      const res = await adminUserAPI.resetPassword(user.learner_id);
      const pwd = res?.data?.temporary_password;
      if (!pwd) {
        showMsg('Reset succeeded but no password was returned. Try again or check API logs.', true);
        return;
      }
      setTempPassword(pwd);
      setTab('password');
      showMsg('Password reset. Copy the temporary password below — it is only shown once.');
      // Refresh the list but keep this drawer open so the password stays visible.
      onRefreshList?.();
    } catch (e) {
      showMsg(e?.response?.data?.detail || 'Reset failed.', true);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async () => {
    const wasActive = user.is_active !== false;
    const action = wasActive ? 'deactivate' : 'reactivate';
    if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} this user?`)) return;
    setSaving(true);
    try {
      if (wasActive) {
        await adminUserAPI.deactivate(user.learner_id);
        showMsg('Account deactivated. It no longer appears under Active; switch Status to Deactivated to find it.');
      } else {
        await adminUserAPI.reactivate(user.learner_id);
        showMsg('Account reactivated.');
      }
      onUpdated();
    } catch (e) {
      showMsg(e?.response?.data?.detail || 'Action failed.', true);
    } finally {
      setSaving(false);
    }
  };

  const deleteAccount = async () => {
    const email = (user.email || '').trim().toLowerCase();
    if (deleteConfirm.trim().toLowerCase() !== email) {
      showMsg('Type the account email exactly to confirm permanent deletion.', true);
      return;
    }
    if (!window.confirm(
      `Permanently delete ${user.email}? This cannot be undone. Prefer Deactivate if you only want to block login.`,
    )) return;
    setSaving(true);
    try {
      await adminUserAPI.deleteUser(user.learner_id, user.email);
      showMsg('Account permanently deleted.');
      onUpdated();
    } catch (e) {
      showMsg(e?.response?.data?.detail || 'Delete failed.', true);
    } finally {
      setSaving(false);
    }
  };

  const assignTrack = async () => {
    if (!trackForm.domain_id || !trackForm.track_id) {
      showMsg('Domain and Track are required.', true); return;
    }
    setSaving(true);
    try {
      await adminUserAPI.assignTrack(user.learner_id, trackForm);
      showMsg('Track assigned.');
      onUpdated();
    } catch (e) {
      showMsg(e?.response?.data?.detail || 'Assign failed.', true);
    } finally {
      setSaving(false);
    }
  };

  const drawerStyle = {
    position: 'fixed', top: 0, right: 0, bottom: 0, width: 460,
    background: C.surface, borderLeft: `1px solid ${C.border}`,
    zIndex: 1000, display: 'flex', flexDirection: 'column',
    boxShadow: '-4px 0 24px rgba(0,0,0,0.4)',
  };

  const inputStyle = {
    width: '100%', background: C.bg, border: `1px solid ${C.border}`,
    borderRadius: 6, padding: '8px 10px', color: C.cream, fontSize: 13,
    boxSizing: 'border-box',
  };

  const btnStyle = (variant = 'primary') => ({
    padding: '8px 16px', borderRadius: 6, border: 'none', cursor: 'pointer',
    fontSize: 13, fontWeight: 600,
    background: variant === 'primary' ? C.accent : variant === 'danger' ? '#991b1b' : C.border,
    color: C.cream,
  });

  const tabs = ['profile', 'password', 'track', 'status'];

  return (
    <div style={drawerStyle}>
      {/* Header */}
      <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontWeight: 700, color: C.cream, fontSize: 15 }}>{user.display_name}</div>
          <div style={{ color: C.muted, fontSize: 12 }}>{user.email}</div>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 20 }}>×</button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: `1px solid ${C.border}` }}>
        {tabs.map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            flex: 1, padding: '10px 4px', background: 'none', border: 'none',
            color: tab === t ? C.peach : C.muted, fontSize: 12, cursor: 'pointer',
            borderBottom: tab === t ? `2px solid ${C.peach}` : '2px solid transparent',
            fontWeight: tab === t ? 700 : 400, textTransform: 'capitalize',
          }}>
            {t === 'password' ? 'Reset PW' : t}
          </button>
        ))}
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
        {msg && (
          <div style={{
            background: msg.isError ? '#7f1d1d' : '#14532d',
            color: C.cream, padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 12,
          }}>
            {msg.text}
          </div>
        )}

        {tab === 'profile' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <label style={{ color: C.muted, fontSize: 12 }}>Display Name
              <input style={inputStyle} value={profileForm.display_name}
                onChange={e => setProfileForm(p => ({ ...p, display_name: e.target.value }))} />
            </label>
            <label style={{ color: C.muted, fontSize: 12 }}>Email
              <input style={inputStyle} value={profileForm.email}
                onChange={e => setProfileForm(p => ({ ...p, email: e.target.value }))} />
            </label>
            <label style={{ color: C.muted, fontSize: 12 }}>Language
              <input style={inputStyle} value={profileForm.preferred_language}
                onChange={e => setProfileForm(p => ({ ...p, preferred_language: e.target.value }))} />
            </label>
            <label style={{ color: C.muted, fontSize: 12 }}>Role
              <select style={inputStyle} value={profileForm.role}
                onChange={e => setProfileForm(p => ({ ...p, role: e.target.value }))}>
                {ROLE_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </label>
            <button style={btnStyle()} onClick={saveProfile} disabled={saving}>
              {saving ? 'Saving…' : 'Save Profile'}
            </button>
            <div data-testid="user-domains" style={{ color: C.muted, fontSize: 12 }}>
              Domains
              {scopeDomains.map((d) => (
                <label key={d.domain_id} style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6, color: C.cream }}>
                  <input
                    type="checkbox"
                    checked={domainSel.has(d.domain_id)}
                    onChange={(e) => setDomainSel((prev) => {
                      const next = new Set(prev);
                      if (e.target.checked) next.add(d.domain_id); else next.delete(d.domain_id);
                      return next;
                    })}
                  />
                  {d.display_name || d.domain_id}
                </label>
              ))}
              {!scopeDomains.length && <div style={{ marginTop: 6 }}>No domains available to you.</div>}
            </div>
            <button style={btnStyle()} onClick={saveDomains} disabled={saving || !scopeDomains.length}>
              Save Domains
            </button>
          </div>
        )}

        {tab === 'password' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p style={{ color: C.muted, fontSize: 13, margin: 0 }}>
              Generate a temporary password for this user. They will be required to change it on next login.
            </p>
            <button style={btnStyle('danger')} onClick={resetPassword} disabled={saving}>
              {saving ? 'Resetting…' : 'Reset Password'}
            </button>
            {tempPassword && (
              <div style={{ background: '#1c1917', border: `1px solid ${C.peach}`, borderRadius: 8, padding: 14 }}>
                <div style={{ color: C.peach, fontWeight: 700, fontSize: 12, marginBottom: 6 }}>
                  ⚠ Temporary Password — share securely, store nowhere
                </div>
                <div style={{ fontFamily: 'monospace', fontSize: 16, color: C.cream, letterSpacing: 2 }}>
                  {tempPassword}
                </div>
                <button
                  style={{ ...btnStyle(), marginTop: 10, fontSize: 11 }}
                  onClick={() => navigator.clipboard.writeText(tempPassword)}
                >
                  Copy
                </button>
              </div>
            )}
          </div>
        )}

        {tab === 'track' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p style={{ color: C.muted, fontSize: 13, margin: 0 }}>
              Assign this learner to a specific curriculum track within a domain.
            </p>
            <label style={{ color: C.muted, fontSize: 12 }}>Domain *
              {scopeDomains.length ? (
                <select
                  style={inputStyle}
                  value={trackForm.domain_id}
                  onChange={e => setTrackForm(p => ({ ...p, domain_id: e.target.value }))}
                >
                  <option value="">Select domain…</option>
                  {scopeDomains.map((d) => (
                    <option key={d.domain_id} value={d.domain_id}>
                      {d.display_name || d.domain_id}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  style={inputStyle}
                  placeholder="ai"
                  value={trackForm.domain_id}
                  onChange={e => setTrackForm(p => ({ ...p, domain_id: e.target.value }))}
                />
              )}
            </label>
            <label style={{ color: C.muted, fontSize: 12 }}>Track *
              <select
                style={inputStyle}
                value={trackForm.track_id}
                onChange={e => setTrackForm(p => ({ ...p, track_id: e.target.value }))}
              >
                <option value="">Select track…</option>
                {AI_TRACK_OPTIONS.map((t) => (
                  <option key={t.track_id} value={t.track_id}>{t.display_name}</option>
                ))}
              </select>
            </label>
            <label style={{ color: C.muted, fontSize: 12 }}>Org (optional)
              {orgOptions.length ? (
                <select
                  style={inputStyle}
                  value={trackForm.org_id}
                  onChange={e => setTrackForm(p => ({ ...p, org_id: e.target.value }))}
                >
                  <option value="">None</option>
                  {orgOptions.map((o) => (
                    <option key={o.org_id} value={o.org_id}>
                      {o.display_name || o.org_id}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  style={inputStyle}
                  placeholder="e.g. org_ffd"
                  value={trackForm.org_id}
                  onChange={e => setTrackForm(p => ({ ...p, org_id: e.target.value }))}
                />
              )}
            </label>
            <button style={btnStyle()} onClick={assignTrack} disabled={saving}>
              {saving ? 'Assigning…' : 'Assign Track'}
            </button>
          </div>
        )}

        {tab === 'status' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ color: C.cream, fontSize: 14 }}>
              Current status: <StatusDot isActive={user.is_active !== false} />
              <strong>{user.is_active !== false ? 'Active' : 'Deactivated'}</strong>
            </div>
            <p style={{ color: C.muted, fontSize: 13, margin: 0 }}>
              {user.is_active !== false
                ? 'Deactivating prevents the user from logging in. Their data is retained.'
                : 'Reactivating restores login access.'}
            </p>
            <button
              style={btnStyle(user.is_active !== false ? 'danger' : 'primary')}
              onClick={toggleActive} disabled={saving}
            >
              {saving ? '…' : user.is_active !== false ? 'Deactivate Account' : 'Reactivate Account'}
            </button>

            <div style={{ borderTop: `1px solid ${C.border}`, marginTop: 8, paddingTop: 16 }}>
              <div style={{ color: C.red, fontWeight: 700, fontSize: 13, marginBottom: 6 }}>
                Permanent delete
              </div>
              <p style={{ color: C.muted, fontSize: 12, margin: '0 0 10px' }}>
                Removes the account permanently. Prefer deactivate for reversible lockouts.
                Type the email below to confirm.
              </p>
              <input
                style={inputStyle}
                placeholder={user.email}
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
              />
              <button
                style={{ ...btnStyle('danger'), marginTop: 10, opacity: deleteConfirm.trim().toLowerCase() === (user.email || '').toLowerCase() ? 1 : 0.45 }}
                onClick={deleteAccount}
                disabled={saving || deleteConfirm.trim().toLowerCase() !== (user.email || '').toLowerCase()}
              >
                {saving ? 'Deleting…' : 'Permanently Delete User'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Create User Modal ──────────────────────────────────────────────────────────

export function CreateUserModal({ onClose, onDone }) {
  const { isPlatformAdmin } = useAuth();
  const { assignableDomains: scopeDomains, orgIds, unrestricted } = useOrgScope();
  const [form, setForm] = useState({
    email: '',
    display_name: '',
    role: 'learner',
    domain_ids: [],
    track_id: '',
    org_id: '',
    domain_role: 'domain_editor',
  });
  const [orgOptions, setOrgOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const roleChoices = isPlatformAdmin
    ? ROLE_OPTIONS
    : ROLE_OPTIONS.filter((r) => r !== 'platform_admin' && r !== 'owner');

  const needsDomainEntitlement = ['coach', 'coach_learner', 'administrator'].includes(form.role);

  useEffect(() => {
    adminOrgAPI.listOrgs()
      .then(({ data }) => {
        const list = Array.isArray(data?.orgs) ? data.orgs : (Array.isArray(data) ? data : []);
        const allowed = unrestricted || !orgIds.length
          ? list
          : list.filter((o) => orgIds.includes(o.org_id));
        setOrgOptions(allowed);
      })
      .catch(() => setOrgOptions([]));
  }, [orgIds, unrestricted]);

  const inputStyle = {
    width: '100%', background: C.bg, border: `1px solid ${C.border}`,
    borderRadius: 6, padding: '8px 10px', color: C.cream, fontSize: 13,
    boxSizing: 'border-box',
  };

  const setField = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

  const submit = async () => {
    if (!form.email.trim() || !form.display_name.trim()) {
      setError('Email and full name are required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        email: form.email.trim().toLowerCase(),
        display_name: form.display_name.trim(),
        role: form.role,
      };
      if (form.domain_ids.length) {
        payload.domain_ids = form.domain_ids;
        payload.domain_id = form.domain_ids[0];
      }
      if (form.track_id.trim()) payload.track_id = form.track_id.trim();
      if (form.org_id.trim()) payload.org_id = form.org_id.trim();
      if (needsDomainEntitlement && form.domain_ids.length) {
        payload.domain_role = form.domain_role;
      }
      const res = await adminUserAPI.createUser(payload);
      setResult(res.data);
      onDone?.();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to create user.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1100,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12,
        padding: 28, width: 520, maxWidth: '92vw', maxHeight: '90vh', overflow: 'auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
          <h3 style={{ color: C.cream, margin: 0, fontSize: 16 }}>Create User</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 20 }}>×</button>
        </div>
        <p style={{ color: C.muted, fontSize: 13, marginTop: 0 }}>
          Create any account type. A temporary password is generated — share it securely.
        </p>

        {result ? (
          <div style={{ background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 8, padding: 14 }}>
            <div style={{ fontWeight: 700, color: C.green, marginBottom: 6 }}>
              Created {result.role}: {result.display_name}
            </div>
            <div style={{ color: C.muted, fontSize: 13 }}>{result.email}</div>
            <div style={{ marginTop: 12, fontSize: 13, color: C.cream }}>
              <strong>Temporary password (copy now):</strong>
              <code style={{ display: 'block', marginTop: 6, wordBreak: 'break-all', color: C.peach }}>
                {result.temporary_password}
              </code>
            </div>
            <button
              onClick={onClose}
              style={{
                marginTop: 14, padding: '8px 16px', borderRadius: 6, border: 'none',
                background: C.accent, color: C.cream, cursor: 'pointer', fontWeight: 600, fontSize: 13,
              }}
            >
              Done
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <label style={{ color: C.muted, fontSize: 12 }}>Email *
              <input style={inputStyle} type="email" value={form.email} onChange={setField('email')} placeholder="user@example.com" autoFocus />
            </label>
            <label style={{ color: C.muted, fontSize: 12 }}>Full name *
              <input style={inputStyle} value={form.display_name} onChange={setField('display_name')} placeholder="Jane Doe" />
            </label>
            <label style={{ color: C.muted, fontSize: 12 }}>Role *
              <select style={inputStyle} value={form.role} onChange={setField('role')}>
                {roleChoices.map((r) => (
                  <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>
                ))}
              </select>
            </label>
            <label style={{ color: C.muted, fontSize: 12 }}>Domains (optional)
              <select
                multiple
                data-testid="create-user-domains"
                aria-label="Domains"
                style={{ ...inputStyle, minHeight: 96 }}
                value={form.domain_ids}
                onChange={(e) => {
                  const picked = Array.from(e.target.selectedOptions).map((o) => o.value);
                  setForm((p) => ({ ...p, domain_ids: picked }));
                }}
              >
                {scopeDomains.map((d) => (
                  <option key={d.domain_id} value={d.domain_id}>{d.display_name || d.domain_id}</option>
                ))}
              </select>
              <span style={{ display: 'block', marginTop: 4 }}>
                {scopeDomains.length
                  ? 'Hold Ctrl/Cmd to pick several. Only domains you manage are listed.'
                  : 'No domains available to you.'}
              </span>
            </label>
            {needsDomainEntitlement && form.domain_ids.length > 0 && (
              <label style={{ color: C.muted, fontSize: 12 }}>Domain role (for coaches/admins)
                <select style={inputStyle} value={form.domain_role} onChange={setField('domain_role')}>
                  {DOMAIN_ROLE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </label>
            )}
            <label style={{ color: C.muted, fontSize: 12 }}>Track (optional)
              <select style={inputStyle} value={form.track_id} onChange={setField('track_id')}>
                <option value="">None</option>
                {AI_TRACK_OPTIONS.map((t) => (
                  <option key={t.track_id} value={t.track_id}>{t.display_name}</option>
                ))}
              </select>
            </label>
            <label style={{ color: C.muted, fontSize: 12 }}>Organization (optional)
              {orgOptions.length ? (
                <select style={inputStyle} value={form.org_id} onChange={setField('org_id')}>
                  <option value="">None</option>
                  {orgOptions.map((o) => (
                    <option key={o.org_id} value={o.org_id}>{o.display_name || o.name || o.org_id}</option>
                  ))}
                </select>
              ) : (
                <input style={inputStyle} value={form.org_id} onChange={setField('org_id')} placeholder="org_ffd" />
              )}
            </label>

            {error && (
              <div style={{ background: '#7f1d1d', color: C.cream, padding: '8px 12px', borderRadius: 6, fontSize: 12 }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
              <button
                onClick={onClose}
                style={{ padding: '8px 16px', borderRadius: 6, border: `1px solid ${C.border}`, background: 'transparent', color: C.cream, cursor: 'pointer', fontSize: 13 }}
              >
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={saving}
                style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: C.accent, color: C.cream, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}
              >
                {saving ? 'Creating…' : 'Create User'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Bulk Upload Panel ──────────────────────────────────────────────────────────

function BulkUploadPanel({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef();

  const upload = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await adminUserAPI.bulkUpload(fd);
      setResult(res.data);
    } catch (e) {
      setResult({ error: e?.response?.data?.detail || 'Upload failed' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1100,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12,
        padding: 28, width: 480, maxWidth: '90vw',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ color: C.cream, margin: 0, fontSize: 16 }}>Bulk Import Users</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 20 }}>×</button>
        </div>

        <p style={{ color: C.muted, fontSize: 13, marginTop: 0 }}>
          Upload an XLSX file with columns: <strong style={{ color: C.peach }}>email, display_name</strong>,
          role, org_id, domain_id, track_id, temp_password
        </p>

        <div
          style={{
            border: `2px dashed ${C.border}`, borderRadius: 8, padding: 24,
            textAlign: 'center', cursor: 'pointer', marginBottom: 16,
            color: file ? C.cream : C.muted, fontSize: 13,
          }}
          onClick={() => fileRef.current?.click()}
        >
          {file ? `✓ ${file.name}` : 'Click to select XLSX file'}
          <input ref={fileRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }}
            onChange={e => { setFile(e.target.files[0]); setResult(null); }} />
        </div>

        {result && !result.error && (
          <div style={{ background: '#14532d', borderRadius: 8, padding: 12, marginBottom: 12, fontSize: 12, color: C.cream }}>
            <strong>Created:</strong> {result.created} &nbsp;
            <strong>Skipped:</strong> {result.skipped} &nbsp;
            {result.errors?.length > 0 && <><strong style={{ color: C.red }}>Errors:</strong> {result.errors.length}</>}
            {result.errors?.map((e, i) => (
              <div key={i} style={{ color: C.red, marginTop: 4 }}>Row {e.row}: {e.reason}</div>
            ))}
          </div>
        )}
        {result?.error && (
          <div style={{ background: '#7f1d1d', borderRadius: 8, padding: 12, marginBottom: 12, fontSize: 12, color: C.cream }}>
            {result.error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: C.border, color: C.cream, cursor: 'pointer', fontSize: 13 }}>
            {result ? 'Done' : 'Cancel'}
          </button>
          {!result && (
            <button onClick={upload} disabled={!file || loading}
              style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: C.accent, color: C.cream, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              {loading ? 'Importing…' : 'Import'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export default function AdminUsersPage() {
  const { user } = useAuth();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showBulk, setShowBulk] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  // Filters
  const [roleFilter, setRoleFilter] = useState('');
  const [domainFilter, setDomainFilter] = useState('');
  // Default to Active so deactivate removes the row from the main list.
  const [activeFilter, setActiveFilter] = useState('true');
  const [search, setSearch] = useState('');

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (roleFilter) params.role = roleFilter;
      if (domainFilter) params.domain = domainFilter;
      if (activeFilter !== '') params.is_active = activeFilter === 'true';
      const res = await adminUserAPI.listUsers(params);
      setUsers(res.data.users || []);
    } catch {
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [roleFilter, domainFilter, activeFilter]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const displayed = users.filter(u => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (u.email || '').toLowerCase().includes(q) || (u.display_name || '').toLowerCase().includes(q);
  });

  const filterStyle = {
    background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6,
    padding: '6px 10px', color: C.cream, fontSize: 13,
  };

  return (
    <CoachLayout>
      <div style={{ padding: 24, background: C.bg, minHeight: '100vh', color: C.cream }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: C.cream }}>User Management</h1>
            <p style={{ margin: '4px 0 0', color: C.muted, fontSize: 13 }}>
              {users.length} accounts · Create any role, edit profiles, deactivate or permanently delete
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => setShowCreate(true)}
              style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: C.accent, color: C.cream, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
              + Create User
            </button>
            <button
              onClick={() => setShowBulk(true)}
              style={{ padding: '9px 18px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, color: C.cream, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
              + Bulk Import
            </button>
          </div>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          <input placeholder="Search by name or email…" value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ ...filterStyle, flex: '1 1 200px' }} />
          <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} style={filterStyle}>
            <option value="">All roles</option>
            {ROLE_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
          <input placeholder="ai" value={domainFilter}
            onChange={e => setDomainFilter(e.target.value)} style={{ ...filterStyle, width: 160 }} />
          <select value={activeFilter} onChange={e => setActiveFilter(e.target.value)} style={filterStyle}>
            <option value="">All statuses</option>
            <option value="true">Active</option>
            <option value="false">Deactivated</option>
          </select>
          <button onClick={fetchUsers} style={{ ...filterStyle, cursor: 'pointer', background: C.surface }}>
            Refresh
          </button>
        </div>

        {/* Table */}
        {loading ? (
          <div style={{ color: C.muted, textAlign: 'center', padding: 40 }}>Loading users…</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${C.border}`, color: C.muted, textAlign: 'left' }}>
                  {['Status', 'Name', 'Email', 'Role', 'Domains', 'Created', ''].map(h => (
                    <th key={h} style={{ padding: '8px 10px', fontWeight: 600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayed.length === 0 && (
                  <tr><td colSpan={7} style={{ textAlign: 'center', color: C.muted, padding: 32 }}>No users found</td></tr>
                )}
                {displayed.map(u => (
                  <tr
                    key={u.learner_id}
                    style={{
                      borderBottom: `1px solid ${C.border}`,
                      cursor: 'pointer',
                      background: selectedUser?.learner_id === u.learner_id ? C.surface : 'transparent',
                    }}
                    onClick={() => setSelectedUser(u)}
                  >
                    <td style={{ padding: '9px 10px' }}>
                      <StatusDot isActive={u.is_active !== false} />
                    </td>
                    <td style={{ padding: '9px 10px', color: C.cream, fontWeight: 600 }}>{u.display_name}</td>
                    <td style={{ padding: '9px 10px', color: C.muted }}>{u.email}</td>
                    <td style={{ padding: '9px 10px' }}><RolePill role={u.role} /></td>
                    <td style={{ padding: '9px 10px', color: C.muted, fontSize: 11 }}>
                      {(u.enrolled_domains || []).join(', ') || '—'}
                    </td>
                    <td style={{ padding: '9px 10px', color: C.muted, fontSize: 11 }}>
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td style={{ padding: '9px 10px' }}>
                      <button
                        style={{ background: C.border, border: 'none', borderRadius: 4, color: C.cream, padding: '4px 10px', cursor: 'pointer', fontSize: 11 }}
                        onClick={e => { e.stopPropagation(); setSelectedUser(u); }}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedUser && (
        <UserDrawer
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          onRefreshList={fetchUsers}
          onUpdated={() => {
            fetchUsers();
            setSelectedUser(null);
          }}
        />
      )}

      {showCreate && (
        <CreateUserModal
          onClose={() => setShowCreate(false)}
          onDone={() => { fetchUsers(); }}
        />
      )}

      {showBulk && (
        <BulkUploadPanel
          onClose={() => setShowBulk(false)}
          onDone={() => { setShowBulk(false); fetchUsers(); }}
        />
      )}
    </CoachLayout>
  );
}
